import type { GameState } from '../state';
import { sub, gte } from '../math/num';
import { FUSE_COUNT, FUSE_TARGET, FUSE_DURATION_MS, fuseCost } from '../data/foundry';
import { getPartDef } from '../data/parts/partDefs';
import { rollPartInstance, rollBonusAffix } from '../data/parts/roll';
import { MECH_CORE_INSTANCE_ID } from '../data/mechCore';
import type { Rarity } from '../data/parts/partTypes';
import type { EmitFn } from './events';

/**
 * The Foundry sim (data/foundry.ts holds the numbers). Deterministic: the
 * fused part rolls off the seeded RNG at collection. One forge, one job at a
 * time — choosing what cooks overnight is the decision.
 */

export interface FoundryOpResult {
  ok: boolean;
  reason?: string;
}

/** A fusable pile: duplicates of one def at one rarity, none of them equipped. */
export interface FusableGroup {
  defId: string;
  rarity: Rarity;
  count: number;
}

/** Instances free to melt: in the stash, not wired into the Mind, not the Core. */
function spareInstances(state: GameState, defId: string, rarity: Rarity): string[] {
  const equipped = new Set(state.mind.nodes.map((n) => n.instanceId));
  const prot = new Set(state.inventory.protectedIds);
  return state.inventory.parts
    .filter((p) => p.defId === defId && p.rarity === rarity && p.instanceId !== MECH_CORE_INSTANCE_ID && !equipped.has(p.instanceId) && !prot.has(p.instanceId))
    .map((p) => p.instanceId);
}

/** Every def+rarity pile with enough spare duplicates to fuse. */
export function fusableGroups(state: GameState): FusableGroup[] {
  const equipped = new Set(state.mind.nodes.map((n) => n.instanceId));
  const prot = new Set(state.inventory.protectedIds);
  const counts = new Map<string, FusableGroup>();
  for (const p of state.inventory.parts) {
    if (p.instanceId === MECH_CORE_INSTANCE_ID || equipped.has(p.instanceId) || prot.has(p.instanceId)) continue;
    if (!FUSE_TARGET[p.rarity]) continue; // legendaries have no next rung
    const key = `${p.defId}|${p.rarity}`;
    const g = counts.get(key) ?? { defId: p.defId, rarity: p.rarity, count: 0 };
    g.count += 1;
    counts.set(key, g);
  }
  return [...counts.values()].filter((g) => g.count >= FUSE_COUNT);
}

export function fusionReady(state: GameState): boolean {
  const job = state.foundry.job;
  return !!job && state.elapsedMs >= job.endsMs;
}

/** Feed the forge: consume three spare duplicates + Components, start the timer. */
export function startFusion(state: GameState, defId: string, rarity: Rarity): FoundryOpResult {
  const f = state.foundry;
  if (!f.unlocked) return { ok: false, reason: 'The Foundry is not online.' };
  if (f.job) return { ok: false, reason: 'The forge is already lit. One job at a time.' };
  const target = FUSE_TARGET[rarity];
  if (!target) return { ok: false, reason: 'GOATED parts have no next rung. There is nothing above GOATED.' };
  const spares = spareInstances(state, defId, rarity);
  if (spares.length < FUSE_COUNT) return { ok: false, reason: `Needs ${FUSE_COUNT} spare duplicates (equipped parts do not melt).` };
  const cost = fuseCost(target);
  if (!gte(state.wallet.components, cost)) return { ok: false, reason: 'Not enough Components.' };

  state.wallet.components = sub(state.wallet.components, cost);
  const melt = new Set(spares.slice(0, FUSE_COUNT));
  state.inventory.parts = state.inventory.parts.filter((p) => !melt.has(p.instanceId));
  f.job = { defId, rarity, endsMs: state.elapsedMs + FUSE_DURATION_MS[target] };
  return { ok: true };
}

/** Pull the part from the forge. `hot` = the player nailed the heat-timing
 *  strike (UI minigame, pure player skill): the part rolls one bonus affix.
 *  Emits fusionPoured so the panel can celebrate the result by name. */
export function collectFusion(state: GameState, hot: boolean, emit?: EmitFn): FoundryOpResult {
  const f = state.foundry;
  const job = f.job;
  if (!job) return { ok: false, reason: 'The forge is cold.' };
  if (state.elapsedMs < job.endsMs) return { ok: false, reason: 'Still forging.' };
  if (state.inventory.parts.length >= state.inventory.cap) return { ok: false, reason: 'Stash is full. Make room first.' };
  const target = FUSE_TARGET[job.rarity];
  if (!target) { f.job = null; return { ok: false, reason: 'Corrupt job cleared.' }; }

  const { instance, nextId } = rollPartInstance(job.defId, target, state.rng, state.inventory.nextId);
  state.inventory.nextId = nextId;
  let bonus: { stat: string; value: number } | null = null;
  if (hot) {
    const rolled = rollBonusAffix(getPartDef(job.defId), target, instance.affixes, state.rng);
    if (rolled) {
      instance.affixes.push(rolled);
      bonus = { stat: rolled.stat, value: rolled.value.toNumber() };
    }
  }
  state.inventory.parts.push(instance);
  f.job = null;
  f.fused += 1;
  emit?.({ kind: 'fusionPoured', defId: job.defId, name: getPartDef(job.defId).name, rarity: target, hot, bonus });
  return { ok: true };
}

/** Bring the Foundry online — rides the Sir Loin kill. */
export function unlockFoundry(state: GameState): void {
  if (state.foundry.unlocked) return;
  state.foundry.unlocked = true;
  state.meta.unlocks.foundry = true;
}
